/**
 * PTEL Visor - Infrastructure Layer Component
 * V04 - Capa de infraestructuras para el visor cartográfico
 * 
 * Componente principal que conecta el pipeline de normalización
 * con el visor cartográfico, mostrando las infraestructuras
 * con clustering, iconos tipológicos y popups informativos.
 * 
 * Características:
 * - Renderiza array de NormalizedInfrastructure[]
 * - Transformación automática UTM30 → WGS84
 * - Clustering inteligente con iconos PTEL (V03)
 * - Filtros por tipo y score mínimo
 * - Eventos interactivos (click, hover)
 */

import React, { memo, useMemo, useCallback } from 'react';
import type { NormalizedInfrastructure, InfrastructureType } from '../../types/infrastructure';
import { MarkerCluster, type MarkerClusterOptions } from './MarkerCluster';
import { InfrastructureMarker } from './InfrastructureMarker';
import { toWGS84 } from '../../lib/leaflet-crs';
import './infrastructure.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface InfrastructureLayerProps {
  /** Array de infraestructuras normalizadas */
  data: NormalizedInfrastructure[];
  
  /** Habilitar clustering (default: true) */
  enableClustering?: boolean;
  
  /** Opciones de clustering */
  clusterOptions?: Partial<MarkerClusterOptions>;
  
  /** Filtro por tipo de infraestructura (undefined = todos) */
  typeFilter?: InfrastructureType[];
  
  /** Filtro por score mínimo (0-100) */
  minScore?: number;
  
  /** Filtro por score máximo (0-100) */
  maxScore?: number;
  
  /** Callback al hacer clic en una infraestructura */
  onInfrastructureClick?: (infrastructure: NormalizedInfrastructure) => void;
  
  /** Callback al hover en una infraestructura */
  onInfrastructureHover?: (infrastructure: NormalizedInfrastructure | null) => void;
  
  /** Mostrar popups automáticamente (default: true) */
  showPopups?: boolean;
  
  /** Mostrar tooltips en hover (default: true) */
  showTooltips?: boolean;
  
  /** ID de infraestructura seleccionada */
  selectedId?: string;
  
  /** Opciones del popup */
  popupOptions?: {
    showUTM?: boolean;
    showScore?: boolean;
    showSource?: boolean;
    showAddress?: boolean;
  };
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Opciones por defecto de clustering */
const DEFAULT_CLUSTER_OPTIONS: Partial<MarkerClusterOptions> = {
  maxClusterRadius: 60,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  zoomToBoundsOnClick: true,
};

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Filtra infraestructuras según criterios
 */
function filterInfrastructures(
  data: NormalizedInfrastructure[],
  typeFilter?: InfrastructureType[],
  minScore?: number,
  maxScore?: number
): NormalizedInfrastructure[] {
  return data.filter((infra) => {
    // Filtro por tipo
    if (typeFilter && typeFilter.length > 0) {
      if (!typeFilter.includes(infra.type)) {
        return false;
      }
    }
    
    // Filtro por score mínimo
    if (minScore !== undefined && infra.score < minScore) {
      return false;
    }
    
    // Filtro por score máximo
    if (maxScore !== undefined && infra.score > maxScore) {
      return false;
    }
    
    // Validar coordenadas
    if (typeof infra.x !== 'number' || typeof infra.y !== 'number') {
      return false;
    }
    
    if (isNaN(infra.x) || isNaN(infra.y)) {
      return false;
    }
    
    return true;
  });
}

/**
 * Transforma infraestructuras a formato para MarkerCluster
 */
function _transformToClusterData(
  data: NormalizedInfrastructure[]
): Array<{ lat: number; lng: number; infrastructure: NormalizedInfrastructure }> {
  return data.map((infra) => {
    const [lat, lng] = toWGS84(infra.x, infra.y);
    return {
      lat,
      lng,
      infrastructure: infra,
    };
  });
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Capa de infraestructuras PTEL para el visor cartográfico
 * 
 * Conecta el pipeline de normalización con Leaflet, renderizando
 * las infraestructuras con clustering inteligente y iconos tipológicos.
 * 
 * @example
 * ```tsx
 * <MapContainer center={[37.5, -4.5]} zoom={8}>
 *   <InfrastructureLayer
 *     data={normalizedData}
 *     enableClustering={true}
 *     minScore={50}
 *     typeFilter={[InfrastructureType.HEALTH, InfrastructureType.EDUCATION]}
 *     onInfrastructureClick={(infra) => console.log(infra.name)}
 *   />
 * </MapContainer>
 * ```
 */
export const InfrastructureLayer = memo<InfrastructureLayerProps>(function InfrastructureLayer({
  data,
  enableClustering = true,
  clusterOptions = {},
  typeFilter,
  minScore,
  maxScore,
  onInfrastructureClick,
  onInfrastructureHover,
  showPopups = true,
  showTooltips = true,
  selectedId,
  popupOptions = {},
}) {
  // Filtrar infraestructuras
  const filteredData = useMemo(
    () => filterInfrastructures(data, typeFilter, minScore, maxScore),
    [data, typeFilter, minScore, maxScore]
  );
  
  // Opciones de clustering combinadas
  const mergedClusterOptions = useMemo<MarkerClusterOptions>(
    () => ({
      ...DEFAULT_CLUSTER_OPTIONS,
      ...clusterOptions,
    } as MarkerClusterOptions),
    [clusterOptions]
  );
  
  // Callback para click en marcador
  const handleMarkerClick = useCallback(
    (infrastructure: NormalizedInfrastructure) => {
      onInfrastructureClick?.(infrastructure);
    },
    [onInfrastructureClick]
  );
  
  // Callback para hover en marcador
  const handleMarkerHover = useCallback(
    (infrastructure: NormalizedInfrastructure | null) => {
      onInfrastructureHover?.(infrastructure);
    },
    [onInfrastructureHover]
  );
  
  // Si no hay datos, no renderizar nada
  if (filteredData.length === 0) {
    return null;
  }
  
  // Renderizar marcadores individuales (sin clustering)
  if (!enableClustering) {
    return (
      <>
        {filteredData.map((infrastructure) => (
          <InfrastructureMarker
            key={infrastructure.id}
            infrastructure={infrastructure}
            showPopup={showPopups}
            showTooltip={showTooltips}
            onClick={handleMarkerClick}
            onHover={handleMarkerHover}
            selected={selectedId === infrastructure.id}
            popupOptions={popupOptions}
          />
        ))}
      </>
    );
  }
  
  // Renderizar con clustering
  return (
    <MarkerCluster
      options={mergedClusterOptions}
    >
      {filteredData.map((infrastructure) => (
        <InfrastructureMarker
          key={infrastructure.id}
          infrastructure={infrastructure}
          showPopup={showPopups}
          showTooltip={showTooltips}
          onClick={handleMarkerClick}
          onHover={handleMarkerHover}
          selected={selectedId === infrastructure.id}
          popupOptions={popupOptions}
        />
      ))}
    </MarkerCluster>
  );
});

// =============================================================================
// UTILIDADES EXPORTADAS
// =============================================================================

/**
 * Cuenta infraestructuras por tipo
 */
export function countByType(
  data: NormalizedInfrastructure[]
): Record<InfrastructureType, number> {
  const counts = {} as Record<InfrastructureType, number>;
  
  for (const infra of data) {
    counts[infra.type] = (counts[infra.type] || 0) + 1;
  }
  
  return counts;
}

/**
 * Obtiene estadísticas de scores
 */
export function getScoreStats(data: NormalizedInfrastructure[]): {
  min: number;
  max: number;
  avg: number;
  median: number;
} {
  if (data.length === 0) {
    return { min: 0, max: 0, avg: 0, median: 0 };
  }
  
  const scores = data.map((d) => d.score).sort((a, b) => a - b);
  const sum = scores.reduce((acc, s) => acc + s, 0);
  
  return {
    min: scores[0],
    max: scores[scores.length - 1],
    avg: Math.round(sum / scores.length),
    median: scores[Math.floor(scores.length / 2)],
  };
}

/**
 * Calcula el bounding box de las infraestructuras (en WGS84)
 */
export function getBounds(data: NormalizedInfrastructure[]): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} | null {
  if (data.length === 0) return null;
  
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  
  for (const infra of data) {
    const [lat, lng] = toWGS84(infra.x, infra.y);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }
  
  return { minLat, maxLat, minLng, maxLng };
}

// =============================================================================
// EXPORTS
// =============================================================================

export default InfrastructureLayer;
